import intl from "react-intl-universal";
import { TbLock, TbWallet } from "react-icons/tb";
import { CurrencyIcon } from "../../../../../Components/Atoms/CurrencyIcon";
import { SummaryCard } from "../../../../../Components/Atoms/SummaryCard";
import { pickBaasStableBalances } from "../../../../../Services/baasStablesWalletApi";
import { formatStable } from "../../../Stables/constants";

/**
 * Aggregated StableBalance amounts — never fiat BRL / formatCurrency / R$.
 * Amount first so `BRL1 0.000000` is never misread as `BRL10.000000`.
 */
const formatStableAmount = (value, currency) =>
  `${formatStable(value ?? 0)} ${currency}`;

/** Multi-asset KPI: token-unit sum across stables only (not FX, not fiat). */
const formatApproxTotal = (value) => `≈ ${formatStable(value ?? 0)}`;

const ASSET_ORDER = ["BRL1", "USDC", "USDT", "EURC"];

const sortStableAssets = (entries) =>
  [...entries].sort(([a], [b]) => {
    const rankA = ASSET_ORDER.indexOf(a);
    const rankB = ASSET_ORDER.indexOf(b);
    const safeA = rankA === -1 ? ASSET_ORDER.length : rankA;
    const safeB = rankB === -1 ? ASSET_ORDER.length : rankB;
    if (safeA !== safeB) return safeA - safeB;
    return a.localeCompare(b);
  });

/**
 * Home saldos: 2 KPI cards + compact per-asset rows (scales beyond a few assets).
 * Balances are StableBalance only — fiat AccountBalance BRL never appears as BRL1.
 * Operational limits (LimitAmountCents) must never appear here — use BaasStablesLimitsBar.
 */
export const BaasStablesBalanceCards = ({ balances = {}, iconUrls = {} }) => {
  const stableBalances = pickBaasStableBalances(balances);
  const assets = sortStableAssets(Object.entries(stableBalances));

  const totalAvailable = assets.reduce((sum, [, balance]) => sum + (balance?.available ?? 0), 0);
  const totalBlocked = assets.reduce((sum, [, balance]) => sum + (balance?.blocked ?? 0), 0);

  if (!assets.length) {
    return (
      <div className="app-card app-empty-state">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.stablesWallet.balances.empty")}
        </p>
      </div>
    );
  }

  return (
    <section className="min-w-0 w-full space-y-3">
      <p className="dashboard-section-label">{intl.get("baas.stablesWallet.balances.section")}</p>

      <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2">
        <SummaryCard
          icon={TbWallet}
          title={intl.get("baas.stablesWallet.balances.totalAvailable")}
          value={formatApproxTotal(totalAvailable)}
          footerLabel={intl.get("baas.stablesWallet.balances.totalAvailableSubtext", {
            count: assets.length,
          })}
        />
        <SummaryCard
          icon={TbLock}
          title={intl.get("baas.stablesWallet.balances.totalBlocked")}
          value={formatApproxTotal(totalBlocked)}
          footerLabel={intl.get("baas.stablesWallet.balances.totalBlockedSubtext")}
        />
      </div>

      <div className="app-card overflow-hidden !p-0">
        <div className="hidden border-b border-gray-100 px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:border-brand-dark-border dark:text-gray-500 sm:grid sm:grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] sm:gap-3">
          <span>{intl.get("baas.stablesWallet.balances.columns.asset")}</span>
          <span className="text-right">{intl.get("baas.processing.available")}</span>
          <span className="text-right">{intl.get("baas.processing.blocked")}</span>
          <span className="text-right">{intl.get("baas.processing.total")}</span>
        </div>

        <ul className="divide-y divide-gray-100 dark:divide-brand-dark-border">
          {assets.map(([currency, balance]) => {
            const available = balance?.available ?? 0;
            const blocked = balance?.blocked ?? 0;
            const total = balance?.total ?? available + blocked;

            return (
              <li
                key={currency}
                className="flex flex-col gap-2 px-4 py-3 sm:grid sm:grid-cols-[minmax(0,1.2fr)_repeat(3,minmax(0,1fr))] sm:items-center sm:gap-3"
                data-asset={currency}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="summary-card-icon !h-8 !w-8">
                    <CurrencyIcon
                      asset={currency.toLowerCase()}
                      iconUrl={iconUrls[currency] || iconUrls[currency.toUpperCase()] || null}
                      size={18}
                    />
                  </div>
                  <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {currency}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-2 sm:contents">
                  <div className="min-w-0 sm:text-right">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 sm:hidden">
                      {intl.get("baas.processing.available")}
                    </p>
                    <p className="mt-0.5 break-words text-xs font-semibold text-emerald-700 dark:text-emerald-400 sm:mt-0 sm:text-sm sm:font-medium sm:text-brand-dark sm:dark:text-gray-100">
                      {formatStableAmount(available, currency)}
                    </p>
                  </div>
                  <div className="min-w-0 sm:text-right">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 sm:hidden">
                      {intl.get("baas.processing.blocked")}
                    </p>
                    <p className="mt-0.5 break-words text-xs font-semibold text-amber-700 dark:text-amber-400 sm:mt-0 sm:text-sm sm:font-medium sm:text-brand-dark sm:dark:text-gray-100">
                      {formatStableAmount(blocked, currency)}
                    </p>
                  </div>
                  <div className="min-w-0 sm:text-right">
                    <p className="text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500 sm:hidden">
                      {intl.get("baas.processing.total")}
                    </p>
                    <p className="mt-0.5 break-words text-xs font-bold text-brand-dark dark:text-gray-100 sm:mt-0 sm:text-sm">
                      {formatStableAmount(total, currency)}
                    </p>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};
